import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { FREQUENCY_OPTIONS, STRENGTH_OPTIONS } from '../../../../utils/riskUtils';
import RiskFormModal from './RiskFormModal';
import RiskViewModal from './RiskViewModal';
import { AuthContext } from '../../../../context/AuthContext';
import {
  getAssessEvaluations, deleteAssessEvaluation, toggleAssessMatrixCheck, getSafetyBasicInfo,
  getSafetyCategories, getRiskCauses, getSafetyProcessMasters, getSafetyMasterMappings
} from '../../../../api/safetyAssessApi';
import SafetyMatrixModal from '../../shared/SafetyMatrixModal';

const freqLabel = (v) => { const o = FREQUENCY_OPTIONS.find(o => String(o.value) === String(v)); return o ? `${v}(${o.label.split(' ')[0]})` : '-'; };
const strLabel = (v) => { const o = STRENGTH_OPTIONS.find(o => String(o.value) === String(v)); return o ? `${v}(${o.label.split(' ')[0]})` : '-'; };

const renderFreqBadge = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  return (
    <span className="badge" style={{ backgroundColor: '#94a3b8', color: '#fff', padding: '4px 6px', fontWeight: 'bold', fontSize: '0.7rem', minWidth: 'auto', display: 'inline-block' }}>
      {freqLabel(v)}
    </span>
  );
};

const renderStrBadge = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  return (
    <span className="badge" style={{ backgroundColor: '#94a3b8', color: '#fff', padding: '4px 6px', fontWeight: 'bold', fontSize: '0.7rem', minWidth: 'auto', display: 'inline-block' }}>
      {strLabel(v)}
    </span>
  );
};

const renderRiskBadge = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const score = parseInt(v, 10);
  let bgColor = '#64748b';
  let level = '하';
  if (score === 1 || score === 2) {
    bgColor = '#10b981'; // Green
    level = '하';
  } else if (score === 3 || score === 4) {
    bgColor = '#f59e0b'; // Orange
    level = '중';
  } else if (score === 6 || score === 9) {
    bgColor = '#ef4444'; // Red
    level = '상';
  }
  return (
    <span className="badge" style={{ backgroundColor: bgColor, color: '#fff', padding: '4px 6px', fontWeight: 'bold', fontSize: '0.7rem', minWidth: 'auto', display: 'inline-block' }}>
      {score}({level})
    </span>
  );
};

const renderActionStatusBadge = (status) => {
  const s = status || 'pending';
  let label = '미완료 (대기)';
  let badgeClass = 'sf-badge-secondary';

  if (s === 'completed') {
    badgeClass = 'sf-badge-success';
    label = '완료';
  } else if (s === 'in_progress') {
    badgeClass = 'sf-badge-warning';
    label = '진행중';
  }
  return (
    <span className={`sf-badge ${badgeClass}`} style={{ padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-block' }}>
      {label}
    </span>
  );
};

const AssigneeBadge = ({ assignees }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [direction, setDirection] = React.useState('down');
  const badgeRef = React.useRef(null);

  if (!assignees || assignees.length === 0) return '-';
  const mainText = assignees.length > 1 ? `${assignees[0].name} 외 ${assignees.length - 1}` : assignees[0].name;

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (!badgeRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    const container = badgeRef.current.closest('.table-responsive');
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const spaceBelow = containerRect.bottom - rect.bottom;
      setDirection(spaceBelow < 200 ? 'up' : 'down');
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const popoverStyle = direction === 'up'
    ? { bottom: '125%', left: '50%', transform: 'translateX(-50%)' }
    : { top: '125%', left: '50%', transform: 'translateX(-50%)' };

  const arrowStyle = direction === 'up'
    ? { position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: '0', height: '0', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid #2d3748' }
    : { position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', width: '0', height: '0', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid #2d3748' };

  return (
    <div
      ref={badgeRef}
      className="position-relative d-inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="badge bg-light text-dark border px-2 py-1.5 fw-semibold" style={{ cursor: 'pointer', fontSize: '0.8rem', borderColor: '#cbd5e1' }}>
        <i className="fas fa-user-circle me-1 text-secondary"></i>{mainText}
      </span>

      {isHovered && (
        <div className="assignee-popover shadow-lg rounded-3 p-3 text-start" style={{
          position: 'absolute',
          backgroundColor: '#2d3748',
          color: '#fff',
          zIndex: 1000,
          width: 'max-content',
          maxWidth: '360px',
          ...popoverStyle
        }}>
          <div className="mb-2">
            <span className="badge bg-primary fw-bold" style={{ fontSize: '0.75rem' }}>담당자</span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: assignees.length >= 3 ? 'repeat(3, 1fr)' : `repeat(${assignees.length}, 1fr)`,
            gap: '8px',
            width: '100%',
          }}>
            {assignees.map((a, idx) => (
              <span key={idx} className="badge" style={{ backgroundColor: '#4a5568', color: '#fff', padding: '6px 8px', fontSize: '0.75rem', fontWeight: 'normal', textAlign: 'center', whiteSpace: 'nowrap' }}>
                {a.name}{a.position ? `/${a.position}` : ''}
              </span>
            ))}
          </div>
          <div style={arrowStyle}></div>
        </div>
      )}
    </div>
  );
};

const ProcessBadge = ({ processName }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [direction, setDirection] = React.useState('down');
  const badgeRef = React.useRef(null);

  if (!processName) return '-';
  if (processName === '사업장 공통') {
    return (
      <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        사업장 공통
      </div>
    );
  }
  const processes = processName.split(',').map(p => p.trim()).filter(Boolean);
  if (processes.length === 0) return '-';

  // 1. 공정이 1개일 때: 아이콘 없는 배지 형태 + 말줄임표(...) + 마우스 오버 시 브라우저 기본 툴팁(title)
  if (processes.length === 1) {
    return (
      <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={processes[0]}>
        {processes[0]}
      </div>
    );
  }

  // 2. 공정이 2개 이상일 때: 배지 형태 + '외 N건' + 마우스 오버(호버) 시 검은색 팝업 노출 (벗어나면 소멸)
  const mainText = `${processes[0]} 외 ${processes.length - 1}건`;

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (!badgeRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    const container = badgeRef.current.closest('.table-responsive');
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const spaceBelow = containerRect.bottom - rect.bottom;
      setDirection(spaceBelow < 200 ? 'up' : 'down');
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const popoverStyle = direction === 'up'
    ? { bottom: '125%', left: '50%', transform: 'translateX(-50%)' }
    : { top: '125%', left: '50%', transform: 'translateX(-50%)' };

  const arrowStyle = direction === 'up'
    ? { position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: '0', height: '0', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid #2d3748' }
    : { position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', width: '0', height: '0', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid #2d3748' };

  return (
    <div
      ref={badgeRef}
      className="position-relative d-block"
      style={{ width: '100%' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="d-flex align-items-center" style={{ cursor: 'pointer', width: '100%', overflow: 'hidden' }}>
        <i className="fas fa-tasks me-1 text-secondary" style={{ flexShrink: 0 }}></i>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, textAlign: 'left' }}>
          {mainText}
        </span>
      </div>

      {isHovered && (
        <div className="process-popover shadow-lg rounded-3 p-3 text-start" style={{
          position: 'absolute',
          backgroundColor: '#2d3748',
          color: '#fff',
          zIndex: 1000,
          width: 'max-content',
          maxWidth: '360px',
          ...popoverStyle
        }}>
          <div className="mb-2">
            <span className="badge bg-primary fw-bold" style={{ fontSize: '0.75rem' }}>대상 공정 목록</span>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            maxHeight: '180px',
            overflowY: 'auto',
            maxWidth: '280px',
            paddingRight: '4px'
          }}>
            {processes.map((p, idx) => (
              <span key={idx} className="badge text-start" style={{ backgroundColor: '#4a5568', color: '#fff', padding: '6px 8px', fontSize: '0.75rem', fontWeight: 'normal', whiteSpace: 'normal', wordBreak: 'break-all', display: 'inline-block' }}>
                {p}
              </span>
            ))}
          </div>
          <div style={arrowStyle}></div>
        </div>
      )}
    </div>
  );
};

function SafetyProjectItemGrid({ assessmentId, assessmentName, evalMethod, getGradeInfo, gradeRules = [], projectStatus, hasPermission = false }) {
  const { token, user } = useContext(AuthContext);
  const [useProcess, setUseProcess] = useState(true);

  // 모달 제어 상태
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalType, setModalType] = useState(null); // 'FORM' (등록/수정) or 'VIEW' (상세조회)
  const [showMatrixGuide, setShowMatrixGuide] = useState(false);

  // 전체 매트릭스 확인 관련 상태
  const [showMatrixModal, setShowMatrixModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [causes, setCauses] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [mappings, setMappings] = useState([]);

  // 뷰 모드 및 담당자 권한 상태 (INIT / ALL / MY)
  const [viewMode, setViewMode] = useState('INIT');
  const [hasMyAction, setHasMyAction] = useState(false);

  // 필터링 상태
  const [selectedStatus, setSelectedStatus] = useState('ALL');

  // 검색 상태
  const [searchTarget, setSearchTarget] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearchTarget, setAppliedSearchTarget] = useState('ALL');
  const [appliedSearchText, setAppliedSearchText] = useState('');

  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 데이터 상태
  const [gridItems, setGridItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  const currentUserId = user?.userid || user?.userId || user?.emp_no || user?.id;
  const currentUserName = user?.name || user?.userName;

  // 권한별 초기 viewMode 및 탭 노출 여부 결정
  useEffect(() => {
    const initViewMode = async () => {
      if (!token || !currentUserId || !assessmentId) return;

      try {
        // 먼저 해당 프로젝트 내에 로그인 사용자의 담당 조치 건이 있는지 검사
        const res = await getAssessEvaluations(assessmentId, token, {
          page: 1,
          limit: 1,
          status: 'ALL',
          assigneeUserid: currentUserId,
          assigneeName: currentUserName
        });

        if (res && res.totalCount > 0) {
          // 1. 내 담당 조치 건이 1개 이상 존재함: 관리자 여부와 관계없이 무조건 탭 노출 + '나의 개선 조치' 기본 선택
          setHasMyAction(true);
          setViewMode('MY');
        } else if (hasPermission) {
          // 2. 내 담당 건은 없지만 관리자 그룹인 경우: 탭 노출 + '전체 개선 조치' 기본 선택
          setHasMyAction(false);
          setViewMode('ALL');
        } else {
          // 3. 내 담당 건도 없고 일반 사용자인 경우: 탭 숨김 + '전체 개선 조치' 기본 선택
          setHasMyAction(false);
          setViewMode('ALL');
        }
      } catch (err) {
        setHasMyAction(false);
        setViewMode(hasPermission ? 'ALL' : 'ALL');
      }
    };

    initViewMode();
  }, [assessmentId, token, currentUserId, currentUserName, hasPermission]);

  const fetchGridData = async () => {
    if (viewMode === 'INIT') return;
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        searchTarget: appliedSearchTarget,
        searchInput: appliedSearchText,
        status: selectedStatus
      };

      if (viewMode === 'MY') {
        params.assigneeUserid = currentUserId;
        params.assigneeName = currentUserName;
      }

      const res = await getAssessEvaluations(assessmentId, token, params);
      if (res && res.items) {
        setGridItems(res.items);
        setTotalItems(res.totalCount);
      } else {
        setGridItems([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, appliedSearchTarget, appliedSearchText, viewMode]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [catData, causeData, procData, mapData, basicInfo] = await Promise.all([
          getSafetyCategories(token),
          getRiskCauses('', token),
          getSafetyProcessMasters(token),
          getSafetyMasterMappings(token),
          getSafetyBasicInfo(token)
        ]);

        const colorMap = {
          1: { main: '#7c3aed', sub: '#a78bfa' },
          2: { main: '#f59e0b', sub: '#fbbf24' },
          3: { main: '#059669', sub: '#34d399' },
          4: { main: '#dc2626', sub: '#f87171' },
          5: { main: '#2563eb', sub: '#60a5fa' },
          6: { main: '#0891b2', sub: '#22d3ee' },
          7: { main: '#db2777', sub: '#f472b6' },
          8: { main: '#65a30d', sub: '#a3e635' },
          9: { main: '#475569', sub: '#94a3b8' },
          10: { main: '#9333ea', sub: '#c084fc' },
        };
        const enrichedCategories = (catData || []).map((c, i) => {
          const cColors = colorMap[c.id] || colorMap[(i % 10) + 1];
          return { ...c, color: cColors.main, subColor: cColors.sub };
        });

        setCategories(enrichedCategories);
        setCauses(causeData || []);
        setProcesses(procData || []);
        setMappings(mapData || []);

        if (basicInfo && basicInfo.useProcess !== undefined) {
          setUseProcess(basicInfo.useProcess);
        }
      } catch (err) {
        console.error('설정 조회 실패:', err);
      }
    };
    fetchConfig();
  }, [token]);

  const isCheckedInMatrix = (processId, causeId) => mappings.some(m => m.processId === processId && m.causeId === causeId);

  useEffect(() => {
    fetchGridData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, currentPage, appliedSearchTarget, appliedSearchText, selectedStatus, viewMode]);

  // 1. 필터링 및 검색 수행
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;

  const openViewModal = (item) => {
    setSelectedItem(item);
    setModalType('VIEW');
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setModalType(null);
  };

  const handleSearch = () => {
    setAppliedSearchTarget(searchTarget);
    setAppliedSearchText(searchInput);
  };

  const handleReset = () => {
    setSearchTarget('ALL');
    setSearchInput('');
    setAppliedSearchTarget('ALL');
    setAppliedSearchText('');
  };

  return (
    <div className="sf-card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* 상단 헤더: 딥 인디고 블루(#2b2a6b) 헤더 바 */}
      <div style={{ background: '#2b2a6b', color: '#ffffff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h5 className="fw-bold m-0" style={{ color: '#ffffff', fontSize: '1rem' }}>
          <i className="bi bi-list-check me-2"></i>
          2. 위험성평가 및 조치 리스트
        </h5>
        <div className="d-flex align-items-center gap-2">
          <button
            className="sf-btn sf-btn-sm"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600 }}
            onClick={() => setShowMatrixModal(true)}
          >
            <i className="bi bi-grid-3x3 me-1"></i> 전체 매트릭스 확인
          </button>
          <button className="sf-btn sf-btn-sm" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600 }} onClick={() => setShowMatrixGuide(true)}>
            <i className="bi bi-grid-3x3-gap me-1"></i> 평가 기준표
          </button>
          {hasPermission && (
            <button className="sf-btn sf-btn-white sf-btn-sm" style={{ fontWeight: 700, border: '1px solid transparent' }} onClick={() => { setSelectedItem(null); setModalType('FORM'); }}>
              ✏️ 평가·조치 등록
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '20px' }}>

        {/* 검색/필터 바 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
          {/* 좌측: 조치 상태 드롭다운 & (권한자/담당자 전용) 전체/내담당 토글 탭 */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              style={{ height: '38px', padding: '0 32px 0 12px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', outline: 'none', backgroundColor: '#fff', cursor: 'pointer', appearance: 'none', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px top 50%', backgroundSize: '10px auto', flexShrink: 0 }}
            >
              <option value="ALL">전체 상태</option>
              <option value="pending">미완료 (대기)</option>
              <option value="in_progress">진행중</option>
              <option value="completed">완료</option>
            </select>

            {(hasPermission || hasMyAction) && (
              <div style={{ display: 'flex', background: '#f1f5f9', padding: '3px', borderRadius: '8px', gap: 4, border: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => { setViewMode('ALL'); setCurrentPage(1); }}
                  style={{
                    background: viewMode === 'ALL' ? '#ffffff' : 'transparent',
                    border: 'none', borderRadius: '6px', padding: '5px 12px', fontWeight: viewMode === 'ALL' ? 800 : 600,
                    boxShadow: viewMode === 'ALL' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer',
                    fontSize: '0.8rem', color: viewMode === 'ALL' ? '#1e40af' : '#475569', transition: 'all 0.2s'
                  }}
                >
                  전체 개선 조치
                </button>
                <button
                  type="button"
                  onClick={() => { setViewMode('MY'); setCurrentPage(1); }}
                  style={{
                    background: viewMode === 'MY' ? '#ffffff' : 'transparent',
                    border: 'none', borderRadius: '6px', padding: '5px 12px', fontWeight: viewMode === 'MY' ? 800 : 600,
                    boxShadow: viewMode === 'MY' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer',
                    fontSize: '0.8rem', color: viewMode === 'MY' ? '#1e40af' : '#475569', transition: 'all 0.2s'
                  }}
                >
                  나의 개선 조치
                </button>
              </div>
            )}
          </div>

          {/* 우측: 검색 영역 */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* 검색어 뱃지 (전체 리스트 앞) */}
            {appliedSearchText && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '16px', fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600 }}>
                <span>'{appliedSearchText}' 검색결과</span>
                <i
                  className="fas fa-times"
                  style={{ cursor: 'pointer', background: '#dbeafe', borderRadius: '50%', padding: '2px 4px', fontSize: '0.7rem' }}
                  onClick={handleReset}
                ></i>
              </div>
            )}

            <select
              value={searchTarget}
              onChange={e => setSearchTarget(e.target.value)}
              style={{ height: '38px', padding: '0 32px 0 12px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', outline: 'none', backgroundColor: '#fff', cursor: 'pointer', appearance: 'none', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px top 50%', backgroundSize: '10px auto', flexShrink: 0 }}
            >
              <option value="ALL">전체</option>
              {useProcess && <option value="PROCESS">대상 공정</option>}
              <option value="CATEGORY">유해위험 분류</option>
              <option value="CAUSE">유해위험 원인</option>
              <option value="ACTION">개선대책</option>
              <option value="ASSIGNEE">담당자</option>
            </select>

            <input
              type="text"
              placeholder="검색어 입력"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              style={{ width: '220px', height: '38px', padding: '0 12px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', backgroundColor: '#fff', flexShrink: 1, minWidth: '80px' }}
            />

            <button
              type="button"
              onClick={handleSearch}
              style={{ height: '38px', padding: '0 16px', background: '#64748b', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              검색
            </button>

            <button
              type="button"
              onClick={handleReset}
              style={{ height: '38px', padding: '0 16px', background: 'white', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              초기화
            </button>
          </div>
        </div>

        {/* 평가 그리드 테이블 */}
        <div className="table-responsive" style={{ border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden', minHeight: '320px' }}>
          <table className="sf-table" style={{ fontSize: '0.88rem', tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr style={{ fontWeight: 800 }}>
                <th className="text-center" style={{ width: '45px', minWidth: '45px' }}>No</th>
                <th className="text-center" style={{ width: '70px', minWidth: '70px' }}>상태</th>
                {useProcess && <th style={{ width: '150px', minWidth: '150px' }}>대상 공정</th>}
                <th style={{ width: useProcess ? '120px' : '180px', minWidth: useProcess ? '120px' : '180px' }}>유해위험 분류</th>
                <th style={{ width: useProcess ? '200px' : '250px', minWidth: useProcess ? '200px' : '250px' }}>유해위험 원인</th>
                <th className="text-center" style={{ width: '140px', minWidth: '140px' }}>
                  <div>현재 위험성</div>
                  <div className="d-flex justify-content-around mt-1" style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 'normal' }}>
                    <span>빈도</span><span>강도</span><span>위험성</span>
                  </div>
                </th>
                <th className="text-center" style={{ width: '90px', minWidth: '90px' }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }} title="조치예정일">
                    조치예정일
                  </div>
                </th>
                <th className="text-center" style={{ width: '90px', minWidth: '90px' }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }} title="조치완료일">
                    조치완료일
                  </div>
                </th>
                <th className="text-center" style={{ width: '120px', minWidth: '120px' }}>담당자</th>
                <th className="text-center" style={{ width: '140px', minWidth: '140px' }}>
                  <div>개선 후 위험성</div>
                  <div className="d-flex justify-content-around mt-1" style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 'normal' }}>
                    <span>빈도</span><span>강도</span><span>위험성</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {totalItems === 0 ? (
                <tr>
                  <td colSpan={useProcess ? 10 : 9} className="text-center py-5">
                    <i className="bi bi-inbox fs-1 d-block mb-3 text-secondary"></i>
                    <p className="fw-bold fs-5 text-dark mb-1">등록된 평가 리스트가 없습니다.</p>
                    <p className="small text-muted mb-0">우측 상단의 [평가 등록] 버튼을 눌러 새로운 위험성 평가를 작성해 주세요.</p>
                  </td>
                </tr>
              ) : (
                gridItems.map((item, idx) => {
                  const ev = item.evaluation || {};
                  const displayProcessName = (item.processName === '사업장 전역 (공통)' || !item.processName)
                    ? '사업장 공통'
                    : item.processName;
                  return (
                    <tr key={item.matrixCheckId || idx} style={{ cursor: 'pointer' }} onClick={() => openViewModal(item)}>
                      <td className="text-center fw-bold text-secondary">{indexOfFirstItem + idx + 1}</td>
                      <td className="text-center">
                        {renderActionStatusBadge(ev.actionStatus)}
                      </td>
                      {useProcess && (
                        <td>
                          <ProcessBadge processName={displayProcessName} />
                        </td>
                      )}
                      <td>
                        <div style={{ maxWidth: useProcess ? '120px' : '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.categoryName}>
                          {item.categoryName || '-'}
                        </div>
                      </td>
                      <td>
                        <div style={{ maxWidth: useProcess ? '200px' : '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.causeName}>
                          {item.causeName || '-'}
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1 fw-semibold">
                          <span>{renderFreqBadge(ev.freqBefore)}</span>
                          <span>{renderStrBadge(ev.sevBefore)}</span>
                          <span>{renderRiskBadge(ev.riskBefore)}</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <div style={{ maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 auto' }} title={ev.dueDate ? ev.dueDate.split('T')[0] : '-'}>
                          {ev.dueDate ? ev.dueDate.split('T')[0] : '-'}
                        </div>
                      </td>
                      <td className="text-center">
                        <div style={{ maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 auto' }} title={ev.doneDate ? ev.doneDate.split('T')[0] : '-'}>
                          {ev.doneDate ? ev.doneDate.split('T')[0] : '-'}
                        </div>
                      </td>
                      <td className="text-center"><AssigneeBadge assignees={ev.assignees} /></td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1 fw-semibold">
                          <span>{renderFreqBadge(ev.freqAfter)}</span>
                          <span>{renderStrBadge(ev.sevAfter)}</span>
                          <span>{renderRiskBadge(ev.riskAfter)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 페이징 네비게이션 */}
        <div className="d-flex align-items-center mt-2 pt-2 border-top position-relative" style={{ minHeight: '40px' }}>
          {/* 좌측: 페이지 / 총 건수 */}
          <div style={{
            position: 'absolute', left: '0px',
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            padding: '4px 12px', borderRadius: '20px',
            fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
          }}>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#2563eb' }}>{totalItems === 0 ? 0 : currentPage}</span>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#93c5fd', margin: '0 -2px' }}>/</span>
            <span>{totalItems === 0 ? 0 : Math.max(1, totalPages)}</span>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#bfdbfe', margin: '0 2px' }}>|</span>
            <span>총 {totalItems}건</span>
          </div>

          {/* 중앙: 페이징 버튼 (절대경로 중앙 정렬) */}
          <div className="position-absolute start-50 translate-middle-x" style={{ display: 'flex', gap: '6px' }}>
            {(() => {
              const PAGE_BLOCK_SIZE = 10;
              const safeTotalPages = Math.max(1, totalPages);
              const startPage = Math.floor((currentPage - 1) / PAGE_BLOCK_SIZE) * PAGE_BLOCK_SIZE + 1;
              const endPage = Math.min(startPage + PAGE_BLOCK_SIZE - 1, safeTotalPages);
              const pagesInBlock = Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);

              return (
                <>
                  {/* 처음으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1 || totalItems === 0}
                    style={{
                      opacity: (currentPage === 1 || totalItems === 0) ? 0.4 : 1,
                      cursor: (currentPage === 1 || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === 1 || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="처음으로"
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                  {/* 이전 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.max(startPage - 1, 1))}
                    disabled={startPage === 1 || totalItems === 0}
                    style={{
                      opacity: (startPage === 1 || totalItems === 0) ? 0.4 : 1,
                      cursor: (startPage === 1 || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (startPage === 1 || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="이전 10페이지"
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>

                  {pagesInBlock.map(page => (
                    <button
                      key={page}
                      className={`sf-btn sf-btn-sm ${currentPage === page && totalItems > 0 ? 'sf-btn-primary' : 'sf-btn-outline'}`}
                      onClick={() => setCurrentPage(page)}
                      disabled={totalItems === 0}
                      style={{
                        minWidth: 36, borderRadius: 8,
                        opacity: totalItems === 0 ? 0.5 : 1,
                        cursor: totalItems === 0 ? 'default' : 'pointer',
                        pointerEvents: totalItems === 0 ? 'none' : 'auto'
                      }}
                    >
                      {page}
                    </button>
                  ))}

                  {/* 다음 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.min(endPage + 1, safeTotalPages))}
                    disabled={endPage === safeTotalPages || totalItems === 0}
                    style={{
                      opacity: (endPage === safeTotalPages || totalItems === 0) ? 0.4 : 1,
                      cursor: (endPage === safeTotalPages || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (endPage === safeTotalPages || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="다음 10페이지"
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                  {/* 마지막으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(safeTotalPages)}
                    disabled={currentPage === safeTotalPages || totalItems === 0}
                    style={{
                      opacity: (currentPage === safeTotalPages || totalItems === 0) ? 0.4 : 1,
                      cursor: (currentPage === safeTotalPages || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === safeTotalPages || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="마지막으로"
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* 4.1 등록/수정 모달 연동 */}
      {modalType === 'FORM' && (
        <RiskFormModal
          id={selectedItem && selectedItem.evaluation ? selectedItem.evaluation.id : null}
          assessmentId={assessmentId}
          assessmentName={assessmentName}
          matrixCheckId={selectedItem ? selectedItem.matrixCheckId : null}
          initialData={selectedItem}
          readOnly={!hasPermission}
          onClose={handleCloseModal}
          onSaved={fetchGridData}
        />
      )}

      {/* 4.2 상세 조회 모달 연동 */}
      {modalType === 'VIEW' && selectedItem && (
        <RiskViewModal
          initialData={{ ...selectedItem, assessmentName }}
          onClose={handleCloseModal}
          hasPermission={hasPermission}
          onEdit={(item) => {
            setModalType(null);
            setTimeout(() => {
              setSelectedItem(item);
              setModalType('FORM');
            }, 100);
          }}
          onDelete={async (item) => {
            if (!window.confirm('평가 정보를 삭제하시겠습니까?')) return;
            try {
              if (item.evaluation && item.evaluation.id) {
                await deleteAssessEvaluation(item.evaluation.id, token);
              } else {
                await toggleAssessMatrixCheck({
                  assessmentId,
                  processId: item.processId,
                  causeId: item.causeId,
                  checked: false
                }, token);
              }
              fetchGridData();
              handleCloseModal();
            } catch (err) {
              console.error(err);
              alert('삭제 실패');
            }
          }}
        />
      )}

      {/* 4.3 평가 기준표 모달 포탈 연동 */}
      {showMatrixGuide && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
          }}
          onClick={() => setShowMatrixGuide(false)}
        >
          <div
            style={{
              position: 'relative', width: '100%', maxWidth: evalMethod === '3x3' ? '440px' : '640px',
              background: '#fff', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
              overflow: 'hidden', display: 'flex', flexDirection: 'column'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: '#fff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}><i className="bi bi-grid-3x3-gap me-2"></i>위험성평가 기준표 ({evalMethod === '3x3' ? '3x3 행렬' : '5x5 행렬'})</h5>
              <button onClick={() => setShowMatrixGuide(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
            </div>

            <div style={{ padding: '24px', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* 상단 안내 */}
              <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h6 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>위험성 등급 산정 기준</h6>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {gradeRules.map((rule, idx) => (
                    <span key={idx} className="sf-badge" style={{ background: `${rule.color}15`, color: rule.color, border: `1px solid ${rule.color}30`, fontSize: '0.8rem', padding: '4px 10px', fontWeight: 700 }}>
                      {rule.min}~{rule.max}점: {rule.grade}
                    </span>
                  ))}
                </div>
              </div>

              {/* 매트릭스 표 */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', background: '#fff', padding: '24px 20px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'auto', width: '100%' }}>
                {(() => {
                  const size = evalMethod === '3x3' ? 3 : 5;
                  const arr = Array.from({ length: size }, (_, i) => i + 1);
                  const rArr = [...arr].reverse();

                  const colLabels3x3 = { 1: '1점 (경미)', 2: '2점 (보통)', 3: '3점 (자주)' };
                  const rowLabels3x3 = { 3: '3점 (중대)', 2: '2점 (보통)', 1: '1점 (경미)' };

                  const colLabels5x5 = { 1: '1(거의 없음)', 2: '2(드물게)', 3: '3(가끔)', 4: '4(자주)', 5: '5(지속적)' };
                  const rowLabels5x5 = { 5: '5(치명적)', 4: '4(중대)', 3: '3(보통)', 2: '2(경미)', 1: '1(무시가능)' };

                  return (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${size + 1}, 1fr)`, gap: '6px', background: '#fff', textAlign: 'center', fontSize: size === 3 ? '0.78rem' : '0.72rem', fontWeight: 800, width: '100%', maxWidth: size === 3 ? '380px' : '580px' }}>
                        {/* 헤더 좌상단 코너 */}
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', minHeight: size === 3 ? '38px' : '46px', height: 'auto', padding: size === 3 ? '4px 2px' : '6px 4px', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>강도 \ 빈도</div>
                        {/* 열 라벨 (빈도) */}
                        {arr.map(c => (
                          <div key={`hc${c}`} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', minHeight: size === 3 ? '38px' : '46px', height: 'auto', padding: size === 3 ? '4px 2px' : '6px 4px', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {size === 3 ? colLabels3x3[c] : colLabels5x5[c]}
                          </div>
                        ))}
                        {/* 행 라벨 및 셀 데이터 */}
                        {rArr.map(r => (
                          <React.Fragment key={`r${r}`}>
                            {/* 행 라벨 (강도) */}
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', minHeight: size === 3 ? '38px' : '46px', height: 'auto', padding: size === 3 ? '4px 2px' : '6px 4px', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {size === 3 ? rowLabels3x3[r] : rowLabels5x5[r]}
                            </div>
                            {/* 셀 */}
                            {arr.map(c => {
                              const score = r * c;
                              const { grade, color } = getGradeInfo(score);
                              const cleanGrade = grade ? grade.replace(/\(\d+단계\)/g, '') : '';
                              return (
                                <div key={`c${c}`} style={{ background: color, color: '#fff', border: `1px solid ${color}`, borderRadius: '8px', minHeight: size === 3 ? '38px' : '46px', height: 'auto', padding: size === 3 ? '4px 2px' : '6px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', textShadow: '0 1px 2px rgba(0,0,0,0.15)', fontSize: size === 3 ? '0.8rem' : '0.8rem', fontWeight: 900, whiteSpace: 'nowrap' }}>
                                  {size === 3 ? `${score}점(${cleanGrade})` : `${score}점`}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>

                      {/* 하단 가이드 문구 */}
                      <div style={{ width: '100%', maxWidth: size === 3 ? '380px' : '580px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', fontSize: '0.825rem', color: '#475569', fontWeight: 600 }}>
                        위험성 점수 = <span style={{ color: '#2563eb', fontWeight: 800 }}>빈도</span> × <span style={{ color: '#ef4444', fontWeight: 800 }}>강도</span> (최소 1점 ~ 최대 {size === 3 ? 9 : 25}점)
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div style={{ padding: '12px 24px', background: '#f1f5f9', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="sf-btn sf-btn-outline" onClick={() => setShowMatrixGuide(false)} style={{ padding: '6px 20px', fontSize: '0.85rem' }}>닫기</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* 4.5 전체 매트릭스 확인 모달 */}
      <SafetyMatrixModal
        show={showMatrixModal}
        onClose={() => setShowMatrixModal(false)}
        categories={categories}
        causes={causes}
        processes={processes}
        isCheckedInMatrix={isCheckedInMatrix}
        useProcess={useProcess}
      />
    </div>
  );
}

export default SafetyProjectItemGrid;
